// Blocco azioni A2A del pannello Invio Pratiche (estratto move-only, Fase 5).
import { Send, FilePenLine, XCircle, Lock } from "lucide-react";
import type { A2APrefill } from "@/types/admin";
import { STATI_CONCLUSI } from "./invioPratiche.shared";
import type { UnifiedRow } from "./invioPratiche.shared";

export interface PannelloAzioniA2AProps {
  prefill: A2APrefill;
  rows: UnifiedRow[]; selected: string;
  sending: boolean; annulling: boolean; jobRunning: boolean; runningSubmit: boolean;
  setConfirm: (c: { msg: string; danger?: boolean; onYes: () => void } | null) => void;
  // a2aOppIdAtteso: l'id A2A che questa pagina vede ("" = nessuna bozza): il backend lo
  // confronta col CRM e rifiuta se la pagina è rimasta indietro.
  onInvia: (submit: boolean, a2aOppIdAtteso: string) => void;
  onAnnulla: () => void;
}

export function PannelloAzioniA2A(props: PannelloAzioniA2AProps) {
  const { prefill, rows, selected, sending, annulling, jobRunning, runningSubmit, setConfirm, onInvia, onAnnulla } = props;
    const busy = sending || annulling || jobRunning;
    const baseOk = prefill.supportato && !busy;
    const selRow = rows.find((r) => r.opp_id === selected);
    const selStato = selRow?.stato;
    // invio definitivo SOLO da bozza pronta: prima si prepara la bozza su A2A
    // (verificabile), poi si invia. Evita il submit "alla cieca" in un passo solo.
    // Gate sulla RIGA (loadList la aggiorna a job finito; il prefill resta stale).
    // esiste già una bozza/pratica su A2A (id presente da lista o prefill): NON si
    // può "preparare" di nuovo — il backend risponderebbe "già inviata" e si entra
    // in loop. Si può solo Inviare (se bozza pronta) o Annullare. La lista (loadList,
    // a job finito) aggiorna l'id anche in stato errore → vale subito dopo un fallito.
    const hasA2A = !!(selRow?.a2a_opp_id || prefill.a2a_opp_id);
    const bozzaPronta = selStato === "bozza" && hasA2A;
    // Pratiche del motore via API o del browser (flag): due strade, «Invia subito» (giro
    // unico, OTP) e «Prepara bozza» (il giro si ferma prima della chiusura, nessun messaggio
    // al cliente). A bozza pronta si riprende QUELLA bozza con «Invia la bozza». Sul browser
    // il flag è vero dal 28/10 (supera la regola del 24/09, «solo da bozza pronta»).
    const invioSubitoAmmesso = prefill.invio_subito_ammesso;
    // «Invia la bozza» SOLO a stato `bozza` (R4: da `errore` non si riprende; l'esito
    // incerto va controllato su A2A prima di ogni altra azione: il backend rifiuta).
    // «Invia subito» solo senza pratica su A2A: un errore SENZA id è un riprovo legittimo
    // del giro unico; con un id resta solo «Annulla pratica A2A». Mai su inviata/in_corso/
    // concluse: sarebbe un secondo invio.
    const giaOltre = selStato === "inviata" || selStato === "in_corso" || STATI_CONCLUSI.has(selStato ?? "");
    const mostraInvio = bozzaPronta || (invioSubitoAmmesso && !hasA2A && !giaOltre);
    const riprendeBozza = bozzaPronta;
    // "in_corso" impiccato (crash a metà invio, mai ripreso dal reinvio normale
    // che cerca solo "da_inviare"): azione dedicata "Sblocca" (reset via annulla
    // esistente), NON un force/re-send.
    const stuckInCorso = selStato === "in_corso";
    // Moduli firmati mancanti (Voltura/Subentro/Nuova attivazione) → blocca OGNI
    // operazione A2A (prepara bozza e invio definitivo): niente POD loccati.
    const moduliMancanti = prefill.moduli_mancanti ?? [];
    // rilevante solo se l'invio è ancora pendente: non su pratiche già su A2A
    // (inviata/accettata/attivata) né durante un job in corso.
    const moduliBloccoRilevante = !STATI_CONCLUSI.has(selStato ?? "")
      && selStato !== "inviata" && selStato !== "in_corso";
    const bloccatoModuli = moduliMancanti.length > 0 && moduliBloccoRilevante;
    const canSubmit = baseOk && prefill.has_documento && mostraInvio && !bloccatoModuli;
    // pratica avviata (id A2A / bozza / inviata) → annullabile. Pratiche CONCLUSE su
    // A2A (accettata/attivata) NON annullabili da qui: contratti vivi, footgun.
    const annullabile = (hasA2A || selStato === "bozza" || selStato === "inviata")
      && !STATI_CONCLUSI.has(selStato ?? "");
    return (
      <div className="space-y-3">
        {bloccatoModuli && (
          <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            Invio bloccato: mancano i moduli firmati ({moduliMancanti.join(", ")}).
            Caricali e assegnane il tipo dal dettaglio pratica.
          </p>
        )}
        {/* Azione PRIMARIA (unica piena del pannello): «Invia la bozza» a bozza pronta,
            «Invia subito» senza bozza sulle pratiche con il flag acceso (motore via API o browser). Conferma
            sempre: parte l'OTP al cliente, non si ritira. Disabilitato senza documento,
            con l'avviso per caricarlo. */}
        {mostraInvio && (
          <div className="space-y-1">
            <button
              type="button"
              disabled={!canSubmit}
              title={bloccatoModuli ? `Moduli firmati mancanti: ${moduliMancanti.join(", ")}` : undefined}
              onClick={() => setConfirm({
                danger: false,
                msg: riprendeBozza
                  ? `La bozza A2A ${selRow?.a2a_opp_id || prefill.a2a_opp_id} viene inviata e al cliente parte l'OTP di accettazione. Confermi?`
                  : "La pratica viene inviata ad A2A e al cliente parte l'OTP di accettazione. Confermi?",
                onYes: () => { setConfirm(null); onInvia(true, riprendeBozza ? (selRow?.a2a_opp_id || prefill.a2a_opp_id || "") : ""); },
              })}
              className="w-full px-4 py-2.5 text-sm bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity disabled:opacity-45 disabled:cursor-not-allowed font-medium cursor-pointer flex items-center justify-center gap-2"
            >
              <Send className="w-4 h-4" />
              <span className="flex flex-col items-center leading-tight">
                <span>{jobRunning && runningSubmit ? "Invio in corso…" : (riprendeBozza ? "Invia la bozza" : "Invia subito")}</span>
                <span className="text-[11px] font-normal opacity-80">parte l'OTP al cliente</span>
              </span>
            </button>
            {!prefill.has_documento && (
              <p className="text-xs text-amber-700 flex items-center gap-1">
                <Lock className="w-3 h-3 shrink-0" /> Carica il documento d'identità su EspoCRM per abilitare bozza e invio.
              </p>
            )}
          </div>
        )}

        {/* Prepara la bozza su A2A: solo se non esiste già una pratica su A2A (id
            assente). Con una bozza/pratica già su A2A (anche in errore o inviata)
            richiamare /invia darebbe "già inviata" (loop): restano Invia/Annulla. Dopo
            Annulla (id azzerato) riappare qui. Senza conferma: la bozza si annulla.
            Sulle pratiche con «Invia subito» ammesso è SECONDARIO (bordo, non pieno): il
            principale resta «Invia subito». Senza documento è disabilitato come l'invio
            (il motore lo carica su A2A nella bozza). */}
        {!hasA2A && !stuckInCorso && (
            <button
              type="button"
              disabled={!baseOk || bloccatoModuli || (invioSubitoAmmesso && !prefill.has_documento)}
              title={bloccatoModuli ? `Moduli firmati mancanti: ${moduliMancanti.join(", ")}` : undefined}
              onClick={() => onInvia(false, "")}
              className={`w-full px-4 py-2.5 text-sm rounded-lg transition-colors disabled:opacity-45 disabled:cursor-not-allowed font-medium cursor-pointer flex items-center justify-center gap-2 ${
                invioSubitoAmmesso
                  ? "border border-foreground/30 bg-background text-foreground hover:bg-muted"
                  : "bg-emerald-600 text-white hover:bg-emerald-700"}`}
            >
              <FilePenLine className="w-4 h-4" />
              {invioSubitoAmmesso ? (
                <span className="flex flex-col items-center leading-tight">
                  <span>{jobRunning && !runningSubmit ? "Preparazione bozza…" : (sending ? "Avvio…" : "Prepara bozza")}</span>
                  <span className="text-[11px] font-normal text-muted-foreground">nessun messaggio al cliente</span>
                </span>
              ) : (
                jobRunning && !runningSubmit ? "Preparazione bozza…" : (sending ? "Avvio…" : "Prepara bozza")
              )}
            </button>
        )}

        {/* Pratica impiccata in "in_corso" (crash a metà invio): niente force/re-send,
            solo reset a "da_inviare" via /annulla esistente (annulla eventuale bozza
            orfana). Il cron di recovery la sblocca comunque da sola dopo 30min; questa
            è la via manuale immediata per l'operatore. */}
        {stuckInCorso && (
          <div className="space-y-1">
            <button
              type="button"
              disabled={busy}
              onClick={() => setConfirm({
                danger: true,
                msg: "Questa pratica è bloccata in \"In corso\" (probabile interruzione imprevista "
                  + "dell'invio). Sbloccarla la riporta a \"Da inviare\": verrà ritentata al prossimo "
                  + "giro ed eventuale bozza orfana su A2A annullata. Confermi?",
                onYes: () => { setConfirm(null); onAnnulla(); },
              })}
              className="w-full px-4 py-2.5 text-sm border border-amber-500 text-amber-700 rounded-lg hover:bg-amber-50 transition-colors disabled:opacity-50 font-medium cursor-pointer flex items-center justify-center gap-2"
            >
              <Lock className="w-4 h-4" />
              {annulling ? "Sblocco…" : "Sblocca"}
            </button>
          </div>
        )}

        {/* Azione DISTRUTTIVA: separata da divisore, per pratiche avviate (bozza/inviata) */}
        {annullabile && (
          <div className="pt-2 border-t border-border">
            <button
              type="button"
              disabled={busy}
              onClick={() => setConfirm({
                danger: true,
                msg: prefill.a2a_opp_id
                  ? `Annullare la pratica A2A ${prefill.a2a_opp_id}? L'opportunità su A2A verrà cancellata e lo stato azzerato.`
                  : "Azzerare lo stato di invio A2A di questa pratica? (Nessuna pratica A2A collegata da cancellare.)",
                onYes: () => { setConfirm(null); onAnnulla(); },
              })}
              className="w-full px-4 py-2 text-sm border border-destructive/50 text-destructive rounded-lg hover:bg-destructive/10 transition-colors disabled:opacity-50 font-medium cursor-pointer flex items-center justify-center gap-2"
            >
              <XCircle className="w-4 h-4" />
              {annulling ? "Annullamento…" : "Annulla pratica A2A"}
            </button>
          </div>
        )}
      </div>
    );
}
